import { FILE_UPLOAD_ERROR_CODES, type FileUploadRejection } from './file-upload.types';
import { type NormalizedAccept } from './normalize-accept';

export interface ValidatePastedFilesOptions {
  accept?: NormalizedAccept;
  multiple: boolean;
  maxFiles: number;
  maxSize?: number;
  minSize?: number;
}

export function validatePastedFiles(
  files: File[],
  options: ValidatePastedFilesOptions
): { accepted: File[]; rejected: FileUploadRejection[] } {
  const { accept, multiple, maxFiles, maxSize, minSize } = options;
  const accepted: File[] = [];
  const rejected: FileUploadRejection[] = [];

  let candidates = files;
  if (!multiple && candidates.length > 1) {
    candidates.slice(1).forEach((file) => {
      rejected.push({
        file,
        errors: [
          {
            code: FILE_UPLOAD_ERROR_CODES.TOO_MANY_FILES,
            message: 'Too many files',
          },
        ],
      });
    });
    candidates = candidates.slice(0, 1);
  }
  if (maxFiles > 0 && candidates.length > maxFiles) {
    candidates.slice(maxFiles).forEach((file) => {
      rejected.push({
        file,
        errors: [
          {
            code: FILE_UPLOAD_ERROR_CODES.TOO_MANY_FILES,
            message: 'Too many files',
          },
        ],
      });
    });
    candidates = candidates.slice(0, maxFiles);
  }

  for (const file of candidates) {
    const errors: { code: string; message: string }[] = [];
    if (maxSize != null && file.size > maxSize) {
      errors.push({
        code: FILE_UPLOAD_ERROR_CODES.FILE_TOO_LARGE,
        message: `File is larger than ${maxSize} bytes`,
      });
    }
    if (minSize != null && minSize > 0 && file.size < minSize) {
      errors.push({
        code: FILE_UPLOAD_ERROR_CODES.FILE_TOO_SMALL,
        message: `File is smaller than ${minSize} bytes`,
      });
    }
    if (accept && !matchesAccept(file, accept)) {
      errors.push({
        code: FILE_UPLOAD_ERROR_CODES.FILE_INVALID_TYPE,
        message: 'File type not accepted',
      });
    }
    if (errors.length > 0) {
      rejected.push({ file, errors });
    } else {
      accepted.push(file);
    }
  }

  return { accepted, rejected };
}

function matchesAccept(file: File, accept: NormalizedAccept): boolean {
  return Object.entries(accept).some(([mime, extensions]) => {
    if (mime.endsWith('/*')) {
      const prefix = mime.slice(0, -1);
      if (file.type.startsWith(prefix)) return true;
    } else if (file.type && file.type === mime) {
      return true;
    }
    const lowerName = file.name.toLowerCase();
    return extensions.some((extension) => lowerName.endsWith(extension.toLowerCase()));
  });
}
